import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { User } from "../App";
import { api } from "../api";
import TimezoneSelect from "../components/TimezoneSelect";
import { useTurnstile } from "../use-turnstile";

function formatMaxRecordingMinutes(secs: number): string {
  if (secs <= 0) return "";
  if (secs % 60 === 0) {
    const m = secs / 60;
    return `${m} minute${m === 1 ? "" : "s"}`;
  }
  const m = secs / 60;
  const rounded = Math.round(m * 10) / 10;
  return `${rounded} minutes`;
}

function FormattedApiError({ message }: { message: string }) {
  const chunks = message
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const lines =
    chunks.length > 1
      ? chunks
      : message.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter(Boolean);
  const display = lines.length > 0 ? lines : [message];
  return (
    <div className="account-feedback account-feedback--error" role="alert">
      {display.map((line, i) => (
        <p key={i}>{line}</p>
      ))}
    </div>
  );
}

export default function Account({
  user,
  onLogout,
  onUserUpdated,
}: {
  user: User;
  onLogout: () => void;
  onUserUpdated: (u: User) => void;
}) {
  const navigate = useNavigate();
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState("");
  const [deleteSectionOpen, setDeleteSectionOpen] = useState(false);
  const isAccessOnly = user.authSource === "access";

  const deleteTurnstile = useTurnstile(turnstileSiteKey, deleteSectionOpen);

  const [timezoneOverrideLocal, setTimezoneOverrideLocal] = useState<string | null>(
    user.timezoneOverride,
  );
  const [tzBusy, setTzBusy] = useState(false);
  const [tzError, setTzError] = useState("");

  useEffect(() => {
    setTimezoneOverrideLocal(user.timezoneOverride);
  }, [user.timezoneOverride, user.id]);

  const timezoneDirty = timezoneOverrideLocal !== user.timezoneOverride;

  const capRec = user.maxRecordingsPer24h;
  const capDur = user.maxRecordingDurationSecs;
  const hasRecordingLimits = capRec > 0 || capDur > 0;

  async function saveTimezone() {
    setTzError("");
    setTzBusy(true);
    const r = await api<{ user: User }>("/api/account/settings", {
      method: "PATCH",
      body: JSON.stringify({ timezoneOverride: timezoneOverrideLocal }),
    });
    setTzBusy(false);
    if (!r.ok) {
      setTzError(r.error || "Could not save timezone.");
      return;
    }
    if (r.data?.user) onUserUpdated(r.data.user);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/auth/config");
        const j = (await r.json()) as { turnstileSiteKey?: string };
        if (!cancelled) setTurnstileSiteKey(j.turnstileSiteKey?.trim() ? j.turnstileSiteKey : "");
      } catch {
        if (!cancelled) setTurnstileSiteKey("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function sendPasswordResetLink() {
    setResetError("");
    setResetSuccess(false);
    setResetBusy(true);
    const r = await api<{ ok?: boolean }>("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email: user.email }),
    });
    setResetBusy(false);
    if (!r.ok) {
      setResetError(r.error || "Could not send reset email.");
      return;
    }
    setResetSuccess(true);
  }

  async function deleteAccount(e: React.FormEvent) {
    e.preventDefault();
    setDeleteError("");
    if (confirmEmail.trim().toLowerCase() !== user.email.toLowerCase()) {
      setDeleteError("Type your email exactly to confirm.");
      return;
    }
    if (!isAccessOnly && deletePassword.length < 8) {
      setDeleteError("Enter your current password (at least 8 characters).");
      return;
    }
    if (deleteTurnstile.required && !deleteTurnstile.getToken()) {
      setDeleteError("Complete the security check before deleting your account.");
      return;
    }
    setDeleteBusy(true);
    const r = await api<{ ok?: boolean; message?: string }>("/api/account", {
      method: "DELETE",
      body: JSON.stringify({
        confirmEmail: confirmEmail.trim(),
        ...(!isAccessOnly ? { password: deletePassword } : {}),
        ...(deleteTurnstile.required ? { turnstileToken: deleteTurnstile.getToken() } : {}),
      }),
    });
    setDeleteBusy(false);
    if (!r.ok) {
      setDeleteError(r.error || "Could not delete account");
      deleteTurnstile.reset();
      return;
    }
    const msg =
      r.data?.message ||
      "Your account is being removed. You will receive an email when it is finished.";
    onLogout();
    navigate("/login", {
      replace: true,
      state: {
        accountDeletionStarted: true,
        accountDeletionMessage: msg,
      },
    });
  }

  return (
    <>
      <h1>Account</h1>
      <div className="card">
        <p>
          Signed in as <strong>{user.email}</strong>
          {isAccessOnly ? (
            <span className="muted" style={{ display: "block", marginTop: "0.35rem" }}>
              SSO via Cloudflare Access — you can still delete your data here; password reset does not
              apply.
            </span>
          ) : null}
        </p>
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <h2>Recording plan</h2>
        {hasRecordingLimits ? (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Your account currently has these recording limits (we enforce them when you save a
              session to your library):
            </p>
            <ul className="account-plan-limits">
              {capRec > 0 ? (
                <li>
                  Up to <strong>{capRec}</strong> finalized recording{capRec === 1 ? "" : "s"} per
                  rolling <strong>24-hour</strong> window.
                </li>
              ) : null}
              {capDur > 0 ? (
                <li>
                  Up to <strong>{formatMaxRecordingMinutes(capDur)}</strong> per recording (when we
                  can detect duration).
                </li>
              ) : null}
            </ul>
            <p className="muted" style={{ marginTop: "1rem" }}>
              <strong>Unlimited</strong> removes both caps — longer logs, no daily finalize quota.
              We&apos;ll offer a paid upgrade here; pricing and checkout are not live yet.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: "0.5rem" }}
              disabled
              aria-disabled="true"
              title="Checkout is not available yet"
            >
              Pay for Unlimited — Coming soon
            </button>
            <p className="muted" style={{ fontSize: "0.85rem", marginTop: "0.5rem", marginBottom: 0 }}>
              You&apos;ll complete payment on a secure link when billing goes live.
            </p>
          </>
        ) : (
          <>
            <p className="muted" style={{ marginTop: 0 }}>
              Your account has <strong>unlimited</strong> finalized recordings and no maximum length
              per recording.
            </p>
            <p className="muted" style={{ marginTop: "0.75rem" }}>
              If we introduce paid tiers later, unlimited will be the paid option; free accounts may
              have daily caps. Invoices and plan changes will show up here when available.
            </p>
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginTop: "0.5rem" }}
              disabled
              aria-disabled="true"
              title="Not available yet"
            >
              Billing &amp; invoices — Coming soon
            </button>
          </>
        )}
      </div>

      <div className="card" style={{ marginTop: "1rem" }}>
        <h2>Time zone</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Dates in your library use your account time zone. We set an initial guess from your
          connection (Cloudflare edge). You can override it below.
        </p>
        <p className="muted" style={{ marginBottom: "0.75rem" }}>
          <strong>Detected from IP / edge:</strong>{" "}
          {user.timezoneDetected ?? "— (not available on this request; try again after browsing the app)"}
        </p>
        <p className="muted" style={{ marginBottom: "0.75rem" }}>
          <strong>Currently showing:</strong> {user.displayTimezone}
        </p>
        <div className="field">
          <label htmlFor="tz-override">Override timezone</label>
          <TimezoneSelect
            id="tz-override"
            value={timezoneOverrideLocal}
            onChange={setTimezoneOverrideLocal}
            disabled={tzBusy}
          />
          <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.35rem" }}>
            Type to filter, then pick a zone. Leave cleared and save to use only IP-based detection.
          </p>
        </div>
        <div className="timezone-account-actions">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={tzBusy || timezoneOverrideLocal === null}
            onClick={() => setTimezoneOverrideLocal(null)}
          >
            Clear override (use IP-based)
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={tzBusy || !timezoneDirty}
            onClick={() => void saveTimezone()}
          >
            {tzBusy ? "Saving…" : "Save timezone"}
          </button>
        </div>
        {tzError ? <p className="error">{tzError}</p> : null}
      </div>

      {!isAccessOnly ? (
        <div className="card" style={{ marginTop: "1rem" }}>
          <h2>Password</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            We&apos;ll send a reset link to <strong>{user.email}</strong>. Mandrill must be configured
            on the server. For abuse protection, successfully sent reset emails are limited to{" "}
            <strong>10 per address per 24 hours</strong>.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={resetBusy}
            onClick={sendPasswordResetLink}
          >
            {resetBusy ? "Sending…" : "Email me a reset link"}
          </button>
          {resetSuccess ? (
            <div className="account-feedback account-feedback--success" role="status">
              <p>
                If delivery succeeds, check your inbox and spam folder. The link expires in about one
                hour.
              </p>
            </div>
          ) : null}
          {resetError ? <FormattedApiError message={resetError} /> : null}
        </div>
      ) : null}

      <div className="card account-delete-card" style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="account-delete-toggle"
          onClick={() => setDeleteSectionOpen((o) => !o)}
          aria-expanded={deleteSectionOpen}
          aria-controls="account-delete-panel"
          id="account-delete-heading"
        >
          <span className="account-delete-toggle-title">Delete account</span>
          <span className="account-delete-toggle-hint muted" aria-hidden>
            {deleteSectionOpen ? "Hide" : "Show"}
          </span>
        </button>
        {deleteSectionOpen ? (
          <div
            id="account-delete-panel"
            className="account-delete-panel"
            role="region"
            aria-labelledby="account-delete-heading"
          >
            <p className="muted" style={{ marginTop: 0 }}>
              After you confirm, you are signed out immediately and cannot sign in until removal
              finishes (usually within a few minutes). Your recordings, vectors, and R2 objects are
              deleted in the background. If email is configured, you will get a message when removal
              is complete. Signing up again with the same email may fail briefly while removal runs.
            </p>
            <form onSubmit={deleteAccount}>
              <div className="field">
                <label htmlFor="del-confirm">Type your email to confirm</label>
                <input
                  id="del-confirm"
                  type="email"
                  autoComplete="off"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={user.email}
                />
              </div>
              {!isAccessOnly ? (
                <div className="field">
                  <label htmlFor="del-password">Current password</label>
                  <input
                    id="del-password"
                    type="password"
                    autoComplete="current-password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                  />
                </div>
              ) : (
                <p className="muted" style={{ fontSize: "0.9rem" }}>
                  SSO account: confirm with your email and the verification widget below (no password
                  on file).
                </p>
              )}
              {deleteTurnstile.required ? (
                <div className="field turnstile-field">
                  <span className="turnstile-label">Verification</span>
                  <div ref={deleteTurnstile.containerRef} className="turnstile-container" />
                </div>
              ) : null}
              {deleteError ? <p className="error">{deleteError}</p> : null}
              <button
                type="submit"
                className="btn btn-danger"
                disabled={deleteBusy || deleteTurnstile.loadingConfig}
              >
                {deleteBusy ? "Starting removal…" : "Delete my account and all content"}
              </button>
            </form>
          </div>
        ) : null}
      </div>
    </>
  );
}
